import { ChangeDetectionStrategy, Component, ElementRef, Injector, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonHeader,
  IonIcon,
  IonInput,
  IonRow,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToolbar,
  LoadingController,
  ModalController,
} from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IonicScrollManagerService, markFormAsInvalid } from '@okcargo-apps/layout-ionic';
import { MessagesUiService } from '@okcargo-apps/messages-ui';
import { IVehicle } from '@okcargo-apps/shared-domain';
import { mapFormValueToVehicle, mapVehicleToFormValue } from '../vehicle-form.mappers';
import { normalizePlateNumber } from '@okcargo-apps/shared-domain';
import { VehiclesMutationsService } from '../vehicles-mutations.service';
import { IVehicleFormValue, VEHICLES_LIST_MESSAGE_OBJECT_ID, VEHICLE_TYPE_OPTIONS } from '../vehicles-store.model';
import { VehiclesStoreService } from '../vehicles-store.service';

@Component({
  selector: 'lib-vehicle-edit-modal',
  templateUrl: './vehicle-edit-modal.component.html',
  styleUrls: ['./vehicle-edit-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    IonContent,
    IonTitle,
    IonIcon,
    IonButtons,
    IonHeader,
    IonButton,
    IonInput,
    IonSelect,
    IonSelectOption,
    IonGrid,
    IonRow,
    IonCol,
    IonFooter,
    IonToolbar,
    TranslateModule,
  ],
})
export class VehicleEditModalComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly injector = inject(Injector);
  private readonly modalCtrl = inject(ModalController);
  private readonly loadingCtrl = inject(LoadingController);
  private readonly translate = inject(TranslateService);
  private readonly messagesUiService = inject(MessagesUiService);
  private readonly ionicScrollManagerService = inject(IonicScrollManagerService);
  private readonly vehiclesStoreService = inject(VehiclesStoreService);
  private readonly vehiclesMutationsService = inject(VehiclesMutationsService);

  // Comes from the component that opens the modal (ModalController componentProps).
  public vehicle: IVehicle | null = null;

  protected readonly typeOptions = VEHICLE_TYPE_OPTIONS;

  protected vehicleForm: FormGroup = this.fb.group({
    plateNumber: ['', Validators.required],
    type: [null, Validators.required],
  });

  public get isEdit(): boolean {
    return this.vehicle !== null;
  }

  public ngOnInit(): void {
    if (!this.vehicle) return;

    this.vehicleForm.patchValue(mapVehicleToFormValue(this.vehicle));
  }

  protected cancel(): Promise<boolean> {
    return this.modalCtrl.dismiss(null, 'cancel');
  }

  /**
   * The plate number only allows uppercase ASCII letters and digits: while typing or pasting,
   * any other character is removed from the control (e.g. `R-1234 abc` -> `R1234ABC`).
   */
  protected onPlateNumberInput(event: Event): void {
    const control = this.vehicleForm.controls['plateNumber'];
    const typed = (event.target as HTMLIonInputElement & { value?: string }).value ?? '';
    const sanitized = normalizePlateNumber(typed);

    if (sanitized !== (control.value ?? '')) {
      control.setValue(sanitized);
    }
  }

  // TODO extract this helper (duplicated in user-edit-modal and the registration forms) to a shared utility.
  protected getErrorMessage(controlName: string): string | null {
    const control = this.vehicleForm.controls[controlName];
    const errors = control?.errors;
    if (!errors) return null;

    if (typeof errors['message'] === 'string') return errors['message'];

    const firstErrorKey = Object.keys(errors)[0];
    const errorData = errors[firstErrorKey];

    if (typeof errorData === 'string') return errorData;
    return errorData?.message ?? 'error.mandatory';
  }

  protected async confirm(): Promise<void> {
    if (this.vehicleForm.invalid) {
      markFormAsInvalid(this.vehicleForm, this.elementRef.nativeElement, this.injector);
      return;
    }

    const loading = await this.loadingCtrl.create({
      message: this.translate.instant('general.send_data'),
    });
    await loading.present();

    try {
      const vehicle = mapFormValueToVehicle(
        this.vehicleForm.getRawValue() as IVehicleFormValue,
        this.vehicle?.id ?? crypto.randomUUID(),
      );
      const wasEdit = this.isEdit;

      if (wasEdit) {
        await this.vehiclesMutationsService.updateVehicle(vehicle);
      } else {
        await this.vehiclesMutationsService.createVehicle(vehicle);
      }

      this.messagesUiService.setMessage({
        objectId: VEHICLES_LIST_MESSAGE_OBJECT_ID,
        message: [
          this.translate.instant(wasEdit ? 'vehicles.updated_message' : 'vehicles.created_message', {
            plate: vehicle.plateNumber,
          }),
        ],
      });

      loading.message = this.translate.instant('general.loading_data');
      await this.vehiclesStoreService.reloadAfterSaveData();
      await this.modalCtrl.dismiss(vehicle, 'confirm');
      setTimeout(() => this.ionicScrollManagerService.scrollToTop(), 300);
    } catch (error) {
      console.warn(error);
    } finally {
      await loading.dismiss();
    }
  }
}
